import { Injectable } from '@angular/core';
import { Auth, User, getAuth, sendEmailVerification, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { deleteDoc, doc, getDoc, getFirestore, setDoc } from 'firebase/firestore';
import { UserModel } from '../user.model';
import { deviceLabel, forgetTrustedDevice, readTrustedDevice, saveTrustedDevice } from './trusted-device';

export type SignInResult =
  | { status: 'challenge-required' }
  | { status: 'enrollment-required'; otpauthUrl: string }
  // Trusted device: signed in without the TOTP code
  | { status: 'signed-in'; user: UserModel };

interface TotpVerification {
  verified: boolean;
  device?: { id: string; token: string } | null;
}

interface EnrollmentStatus {
  enrolled: boolean;
}

interface EnrollmentStart {
  otpauthUrl: string;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class SignInService {
  private pendingEnrollment = false;

  private get auth(): Auth {
    return getAuth();
  }

  private call<Request, Response>(name: string, data: Request): Promise<Response> {
    const callable = httpsCallable<Request, Response>(getFunctions(this.auth.app, 'us-central1'), name);
    return callable(data).then(result => result.data);
  }

  async signIn(email: string, password: string): Promise<SignInResult> {
    this.pendingEnrollment = false;
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    const credential = await signInWithEmailAndPassword(this.auth, email.trim().toLowerCase(), password);
    const user = credential.user;

    if (!user.emailVerified) {
      await sendEmailVerification(user);
      await signOut(this.auth);
      throw new Error('Confirme seu e-mail antes de entrar. Enviamos uma nova mensagem de verificação.');
    }

    await this.ensureProfile(user);
    const enrollment = await this.call<Record<string, never>, EnrollmentStatus>(
      'getTotpEnrollmentStatus', {}
    );

    if (enrollment.enrolled) {
      if (await this.signInWithTrustedDevice(user)) {
        return { status: 'signed-in', user: await this.loadProfile(user) };
      }
      return { status: 'challenge-required' };
    }

    const setup = await this.call<Record<string, never>, EnrollmentStart>('beginTotpEnrollment', {});
    this.pendingEnrollment = true;
    return { status: 'enrollment-required', otpauthUrl: setup.otpauthUrl };
  }

  async completeTotpEnrollment(code: string): Promise<void> {
    if (!this.pendingEnrollment) {
      throw new Error('A sessão de configuração expirou. Entre novamente para configurar o autenticador.');
    }

    await this.call<{ code: string }, { verified: boolean }>('completeTotpEnrollment', { code });
    this.pendingEnrollment = false;
    await this.refreshAuthToken();
    await signOut(this.auth);
  }

  async completeTotpSignIn(code: string, rememberDevice = false): Promise<UserModel> {
    const result = await this.call<{ code: string, rememberDevice: boolean, deviceLabel: string }, TotpVerification>(
      'verifyTotp', { code, rememberDevice, deviceLabel: deviceLabel() }
    );
    await this.refreshAuthToken();

    if (result.device && this.auth.currentUser) {
      saveTrustedDevice({ uid: this.auth.currentUser.uid, deviceId: result.device.id, token: result.device.token });
    }

    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('A sessão de autenticação expirou. Entre novamente.');
    }

    return this.loadProfile(user);
  }

  async cancelPendingSignIn(): Promise<void> {
    this.pendingEnrollment = false;
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    await signOut(this.auth);
  }

  async signOut(): Promise<void> {
    await this.cancelPendingSignIn();
  }

  // The TOTP code is skipped when this browser has a valid trusted device token; otherwise it is forgotten
  private async signInWithTrustedDevice(user: User): Promise<boolean> {
    const device = readTrustedDevice(user.uid);
    if (!device) return false;

    try {
      await this.call<{ deviceId: string, token: string }, { verified: boolean }>(
        'verifyTrustedDevice', { deviceId: device.deviceId, token: device.token }
      );
      await this.refreshAuthToken();
      return true;
    } catch (error) {
      console.info('SignInService -> trusted device not accepted', error);
      forgetTrustedDevice();
      return false;
    }
  }

  private async refreshAuthToken(): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('A sessão de autenticação expirou. Entre novamente.');
    }
    await user.getIdToken(true);
  }

  private async loadProfile(user: User): Promise<UserModel> {
    await this.ensureProfile(user);
    const profile = await getDoc(doc(getFirestore(), 'users', user.uid));
    return this.toUserProfile(user, profile.data());
  }

  private async ensureProfile(user: User): Promise<void> {
    const firestore = getFirestore();
    const profileRef = doc(firestore, 'users', user.uid);
    let profileSnapshot = await getDoc(profileRef);
    let existingProfile = profileSnapshot.data();

    if (!profileSnapshot.exists() && user.email) {
      const legacyRef = doc(firestore, 'users', user.email);
      const legacySnapshot = await getDoc(legacyRef);
      const legacyProfile = legacySnapshot.data() as Partial<UserModel> | undefined;

      if (legacyProfile?.uid === user.uid) {
        existingProfile = legacyProfile;
        await setDoc(profileRef, this.toUserProfile(user, legacyProfile));
        await deleteDoc(legacyRef);
        profileSnapshot = await getDoc(profileRef);
      }
    }

    if (!profileSnapshot.exists() || existingProfile) {
      await setDoc(profileRef, this.toUserProfile(user, existingProfile));
    }
  }

  private toUserProfile(user: User, profile?: Partial<UserModel>): UserModel {
    return {
      uid: user.uid,
      name: profile?.name ?? user.displayName ?? '',
      email: profile?.email ?? user.email ?? '',
      settings: profile?.settings ?? {
        theme: 'system',
        sidenav: { type: 'side', size: 'collaped', status: true },
        notification: 'on'
      }
    };
  }
}
